import { useEffect, useMemo, useRef, useState } from 'react';
import {
	ChatMessageTargetType,
	ChatSocketType,
	JOIN_CHAT_ROOM,
	ON_CHAT,
	LEAVE_CHAT_ROOM,
} from '@sportspredict/shared';
import { useChat } from '@/contexts';

export function useLobbyChatRoom({
	socket,
	lobbyId,
	user,
	unreadCount,
}: {
	socket: any;
	lobbyId: any;
	user: any;
	unreadCount: any;
}) {
	const { setShowViewLastMessages } = useChat();
	const targetId = lobbyId;

	const [tab, setTab] = useState(ChatMessageTargetType.Lobby);

	const targetType = useMemo(
		() =>
			tab === ChatMessageTargetType.Lobby
				? ChatMessageTargetType.Lobby
				: ChatMessageTargetType.Broadcast,
		[tab]
	);

	const [liveMessages, setLiveMessages] = useState<any[]>([]);
	const [unreadCountMessages, setUnreadCountMessages] = useState({
		lobby: 0,
		broadcast: 0,
	});
	const [chatOpen, setChatOpen] = useState(false);
	const [hasUserTimeout, setHasUserTimeout] = useState(false);
	const [hasChatCloseStatus, setHasChatCloseStatus] = useState(false);

	const refetchMessagesRef = useRef<any>(undefined);
	const chatRef = useRef<any>(null);
	const timeoutTimerRef = useRef<any>(undefined);

	useEffect(() => {
		if (!socket || !targetId) return;

		setLiveMessages([]);
		const handler = ({ type, data }: any) => {
			setLiveMessages((prev) => {
				const list = Array.isArray(prev) ? prev : [];

				if (type === ChatSocketType.UpdateMessage) {
					return list.map((m) => (m.id === data.id ? data : m));
				}

				if (type === ChatSocketType.NewMessage) {
					const isTargetMuted =
						data.target_type === ChatMessageTargetType.Lobby
							? chatRef.current?.isLobbyMuted
							: chatRef.current?.isBroadcastMuted;
					if (!isTargetMuted) {
						setUnreadCountMessages((prev) => ({
							lobby: data.target_type === ChatMessageTargetType.Lobby ? prev.lobby + 1 : prev.lobby,
							broadcast:
								data.target_type === ChatMessageTargetType.Broadcast
									? prev.broadcast + 1
									: prev.broadcast,
						}));
					}
					setShowViewLastMessages(true);
					if (list.some((m) => m.id === data.id)) return list;
					return [...list, data];
				}

				if (type === ChatSocketType.SetUserTimeOut) {
					if (data.user_id === user?.id) {
						clearTimeout(timeoutTimerRef.current);

						const expiresAt = new Date(data.timeout_until).getTime();
						const now = Date.now();
						const msLeft = expiresAt - now;

						if (msLeft > 0) {
							setHasUserTimeout(true);
							timeoutTimerRef.current = window.setTimeout(() => setHasUserTimeout(false), msLeft);
						} else {
							setHasUserTimeout(false);
						}
					}
					return list;
				}
				if (type === ChatSocketType.SetChatStatus) {
					setHasChatCloseStatus(!!data.is_chat_closed);
					return list;
				}

				return list;
			});
		};

		socket.emit(JOIN_CHAT_ROOM, { targetId });
		socket.on(ON_CHAT, handler);

		return () => {
			socket.emit(LEAVE_CHAT_ROOM, { targetId });
			socket.off(ON_CHAT, handler);
		};
	}, [setShowViewLastMessages, socket, targetId, user?.id]);

	useEffect(() => {
		const { lobby = 0, broadcast = 0 } = unreadCount || {};
		setUnreadCountMessages({ lobby, broadcast });
	}, [unreadCount]);

	useEffect(() => {
		const original = document.body.style.overflow;
		if (chatOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = original;
		}
		return () => {
			document.body.style.overflow = original;
		};
	}, [chatOpen]);

	return {
		targetId,
		tab,
		setTab,
		targetType,
		liveMessages,
		setLiveMessages,
		unreadCountMessages,
		setUnreadCountMessages,
		chatOpen,
		setChatOpen,
		hasUserTimeout,
		setHasUserTimeout,
		hasChatCloseStatus,
		setHasChatCloseStatus,
		refetchMessagesRef,
		chatRef,
	};
}
